import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { Card } from './CaseUiPrimitives.js';
import {
  getCaseWorkTimer,
  startCaseWorkTimer,
  stopCaseWorkTimer,
  type CaseWorkTimer,
} from '../lib/work-timer-api.js';

/**
 * Krok D6b bod 1 — KARTA „CASOMIRA PRACE" u zakazky.
 *
 * Krok D6 spocital naklad Kompletu z minut cloveka, ale minuty nikdo nemeril
 * (REPORT-step-D6.md ## ZBYVA). Tahle karta je jejich jedina cesta v UI:
 * jedno tlacitko „Spustit", jedno „Zastavit", a soucet minut zakazky, ktery
 * vstupuje do prehledu nakladu provozovatele.
 *
 * STAV SE PLNI V RENDERU, ne v `useEffect` nad props (poucení „web-admin
 * zavod s efektem po pripojeni"): `useEffect` tu nacita ze SERVERU, protoze
 * jiny zdroj pravdy neni.
 *
 * TLACITKO JE VZDY JEN JEDNO: kdyz usek bezi, karta ukaze „Zastavit", jinak
 * „Spustit". Dvojkliknuti nemuze zalozit dva useky ani tak (unikat v DB,
 * migrace 0034) — 409 ze serveru se vypise jako jeho vlastni text.
 */

const cs = { locale: 'cs' } as const;

function tr(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

function czTime(iso: string): string {
  return new Date(iso).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export function WorkTimerCard({ caseId }: { caseId: string }): React.JSX.Element {
  const [timer, setTimer] = React.useState<CaseWorkTimer | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async (): Promise<void> => {
    try {
      setTimer(await getCaseWorkTimer(caseId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [caseId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function run(action: 'start' | 'stop'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (action === 'start') await startCaseWorkTimer(caseId);
      else await stopCaseWorkTimer(caseId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const running = timer?.running ?? null;
  const finished = (timer?.entries ?? []).filter((entry) => entry.stoppedAt !== null).slice(0, 5);

  return (
    <Card
      heading={tr('webAdmin.caseDetail.workTimer.heading')}
      hint={tr('webAdmin.caseDetail.workTimer.hint')}
      testId="case-work-timer"
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          disabled={busy}
          onClick={() => {
            void run(running === null ? 'start' : 'stop');
          }}
          data-testid={running === null ? 'case-work-timer-start' : 'case-work-timer-stop'}
        >
          {running === null
            ? tr('webAdmin.caseDetail.workTimer.start')
            : tr('webAdmin.caseDetail.workTimer.stop')}
        </button>
        <span data-testid="case-work-timer-total">
          {tr('webAdmin.caseDetail.workTimer.total', { minutes: String(timer?.totalMinutes ?? 0) })}
        </span>
      </div>

      {running !== null ? (
        <p style={{ margin: '8px 0 0' }} data-testid="case-work-timer-running">
          {tr('webAdmin.caseDetail.workTimer.running', { since: czTime(running.startedAt) })}
        </p>
      ) : null}

      {error !== null ? (
        <p style={{ margin: '8px 0 0', color: 'var(--rh-danger)' }} data-testid="case-work-timer-error">
          {error}
        </p>
      ) : null}

      {finished.length > 0 ? (
        <div style={{ marginTop: 10 }} data-testid="case-work-timer-entries">
          {finished.map((entry) => (
            <div
              key={entry.id}
              style={{ borderTop: '1px solid var(--rh-rule)', padding: '6px 0', display: 'flex', gap: 10 }}
            >
              <span style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-icon-muted)', minWidth: 130 }}>
                {czTime(entry.startedAt)}
              </span>
              <span style={{ fontSize: 'var(--rh-size-body)' }}>
                {tr('webAdmin.caseDetail.workTimer.entryMinutes', { minutes: String(entry.minutes ?? 0) })}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
